import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../button/button';
import { Card, CardContent, CardHeader, CardTitle } from '../card';
import { Input } from '../input';
import type { Agent } from '../../../lib/types';
import type { ExternalConnection } from '../../../lib/external-agents/types';

const FORMAT_LABELS = {
  json: 'Plain JSON',
  openai: 'OpenAI-compatible',
  anthropic: 'Anthropic-compatible',
} as const;

interface ExternalAgentDetailsProps {
  agent: Agent;
  connection: ExternalConnection | undefined;
  onSaveGreeting: (greeting: string) => Promise<unknown>;
  onClose: () => void;
}

/**
 * An agent behind the customer's own API. Its prompt and model live on their
 * side; connection settings are edited on the Connections page.
 */
export function ExternalAgentDetails({
  agent,
  connection,
  onSaveGreeting,
  onClose,
}: ExternalAgentDetailsProps) {
  const savedGreeting = agent.greeting ?? '';
  const [greeting, setGreeting] = useState(savedGreeting);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => setGreeting(savedGreeting), [savedGreeting]);

  const saveGreeting = async () => {
    setIsSaving(true);
    try {
      await onSaveGreeting(greeting.trim());
      toast.success('Opening message saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const rows: [string, string][] = connection
    ? [
        ['Endpoint', connection.endpoint_url],
        ['Format', FORMAT_LABELS[connection.format]],
        ...(connection.model
          ? [['Model', connection.model] as [string, string]]
          : []),
        [
          'Conversation',
          connection.conversation_mode === 'session'
            ? 'Session ID'
            : 'Full history each time',
        ],
        [
          'Last test passed',
          connection.last_test_passed_at
            ? format(
                new Date(connection.last_test_passed_at),
                'd MMM yyyy, HH:mm',
              )
            : 'Not tested yet',
        ],
      ]
    : [];

  return (
    <Card className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
          {agent.name}
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label="Close"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <p className="text-sm text-prompt-safecomabbey">
          Runs on your own API. Change the endpoint, secret or format on the{' '}
          <Link
            to="/connections"
            className="font-medium text-prompt-safecomoutrageous-orange hover:underline"
          >
            Connections
          </Link>{' '}
          page.
        </p>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="external-agent-greeting"
            className="text-sm font-medium text-prompt-safecomvulcan"
          >
            Opening message
          </label>
          <div className="flex gap-2">
            <Input
              id="external-agent-greeting"
              value={greeting}
              maxLength={500}
              onChange={(event) => setGreeting(event.target.value)}
              placeholder="Optional: your agent's opening message"
              className="bg-prompt-safecomwhite"
            />
            <Button
              size="sm"
              className="h-9"
              disabled={isSaving || greeting.trim() === savedGreeting}
              onClick={saveGreeting}
            >
              Save
            </Button>
          </div>
          <p className="text-xs text-prompt-safecomabbey">
            Optional. If your product shows a first message, add it here. Your
            agent never sends it, but the persona replies as if it did. Leave it
            blank and the persona starts the conversation.
          </p>
        </div>

        {connection ? (
          <dl className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-2 rounded-lg border border-[#00000014] bg-prompt-safecomwhite p-4 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-prompt-safecomabbey">{label}</dt>
                <dd className="break-all font-mono text-xs leading-5 text-prompt-safecomvulcan">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-red-700">
            Connection settings are missing. Remove this agent and connect it
            again.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
